// Per-device manual chooser: a bottom sheet on mobile / popover on desktop (per
// dashboard.css). Shown only for a device with more than one manual — a single
// manual opens directly without the sheet. Lists each manual with revision /
// language / size so the owner grabs the edition matching their firmware.

import { useEffect } from 'react';

import { formatBytes } from '../../lib/deviceSeen';
import type { VesselDevice } from '../../lib/useBoatDevices';
import { CloseIcon, DocIcon } from '../app/icons';

export function ManualSheet({
  device,
  onClose,
}: {
  device: VesselDevice | null;
  onClose: () => void;
}) {
  // Escape closes; hooks run unconditionally (the sheet is always mounted and
  // toggles visibility via the `open` class).
  useEffect(() => {
    if (device === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [device, onClose]);

  const open = device !== null;
  const manuals = device?.manuals ?? [];
  const name = device ? (device.preferredName ?? device.displayName) : '';

  return (
    <>
      <div className={`scrim${open ? ' open' : ''}`} onClick={onClose} />
      <div
        className={`manual-sheet${open ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={device ? `Manuals for ${name}` : 'Choose a manual'}
        data-testid={device ? `manual-sheet-${device.deviceId}` : undefined}
      >
        <div className="sheet-head">
          <div>
            <h3>{name || 'Manuals'}</h3>
            <div className="sub">Pick the revision matching your firmware</div>
          </div>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <div className="sheet-body">
          {manuals.map((m) => {
            const size = m.sizeBytes != null ? formatBytes(m.sizeBytes) : '';
            const spec = [m.revision, m.language, size].filter(Boolean).join(' · ');
            return (
              <div className="sheet-choice" key={m.id} data-testid={`manual-row-${m.id}`}>
                <div className="mr-doc">
                  <DocIcon />
                </div>
                <div className="sc-main">
                  <div className="sc-title">{m.title}</div>
                  {spec ? <div className="sc-spec">{spec}</div> : null}
                </div>
                <a
                  className="btn-open-sm"
                  href={m.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={onClose}
                >
                  Open
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
